import { useCallback, useRef } from 'react';

/**
 * Callback ref that pins a layer to the viewport while its parent scrolls
 * past, like `background-attachment: fixed` (which iOS Safari ignores).
 * Adjacent sections using the same image render one continuous backdrop.
 * The layer's parent must cover the section (e.g. `absolute inset-0`) and the
 * layer itself should be viewport-sized (`h-screen`) at the parent's top.
 *
 * A callback ref (not an effect) so sections that render late — after their
 * collection data arrives — still get wired up.
 */
export function useFixedLayer<T extends HTMLElement>() {
  const cleanupRef = useRef<(() => void) | null>(null);

  return useCallback((layer: T | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    const container = layer?.parentElement;
    if (!layer || !container) return;

    let frame = 0;
    const update = () => {
      frame = 0;
      const top = container.getBoundingClientRect().top;
      layer.style.transform = `translate3d(0, ${(-top).toFixed(1)}px, 0)`;
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    // Capture phase catches scrolling on any container, not just the window.
    window.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule);
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    resizeObserver?.observe(document.body);

    cleanupRef.current = () => {
      window.removeEventListener('scroll', schedule, { capture: true });
      window.removeEventListener('resize', schedule);
      resizeObserver?.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);
}
